import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useApp } from '../context/AppContext';
import { format } from 'date-fns';
import { formatLocalDate } from '../utils/dateUtils';
import { reviewTripMeter } from '../services/api';
import MeterPhotoViewer from '../components/MeterPhotoViewer';

const METER_MISMATCH = 'meter_mismatch';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'warning', label: 'Warning' },
  { value: 'suspicious', label: 'Suspicious' },
  { value: METER_MISMATCH, label: 'Meter mismatch' },
];

const isMeterMismatch = (t) => t.meterOcrStatus === 'mismatch' && !t.meterOcrReviewed;
const isKmAlert = (t) => t.status === 'warning' || t.status === 'suspicious';

export default function Alerts() {
  const { alerts, vehicles, drivers, isAdmin, refresh } = useApp();

  const [photoTrip, setPhotoTrip] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [status, setStatus] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const vehicleOptions = useMemo(() => {
    const seen = new Map();
    alerts.forEach((t) => {
      const id = t.vehicle?._id;
      if (id && !seen.has(String(id))) {
        seen.set(String(id), t.vehicle.name || 'Unknown vehicle');
      }
    });
    vehicles.forEach((v) => {
      if (v._id && !seen.has(String(v._id))) {
        seen.set(String(v._id), v.name);
      }
    });
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [alerts, vehicles]);

  const driverOptions = useMemo(() => {
    const seen = new Map();
    alerts.forEach((t) => {
      const id = t.driver?._id;
      if (id && !seen.has(String(id))) {
        seen.set(String(id), t.driver.name || 'Unknown driver');
      }
    });
    drivers.forEach((d) => {
      if (d._id && !seen.has(String(d._id))) {
        seen.set(String(d._id), d.name);
      }
    });
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [alerts, drivers]);

  const filtered = useMemo(() => {
    return alerts.filter((t) => {
      if (status === METER_MISMATCH) {
        if (!isMeterMismatch(t)) return false;
      } else if (status && t.status !== status) {
        return false;
      }
      if (vehicleId && String(t.vehicle?._id) !== vehicleId) return false;
      if (driverId && String(t.driver?._id) !== driverId) return false;

      const tripDay = formatLocalDate(t.date);
      if (from && tripDay < from) return false;
      if (to && tripDay > to) return false;

      return true;
    });
  }, [alerts, status, vehicleId, driverId, from, to]);

  const hasActiveFilters = Boolean(status || vehicleId || driverId || from || to);

  const clearFilters = () => {
    setStatus('');
    setVehicleId('');
    setDriverId('');
    setFrom('');
    setTo('');
  };

  const markReviewed = async (trip) => {
    setReviewingId(trip._id);
    try {
      await reviewTripMeter(trip._id);
      toast.success('Meter check marked reviewed');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to mark reviewed');
    } finally {
      setReviewingId(null);
    }
  };

  return (
    <>
      <div className="page-header">
        <h2>Alerts ⚑</h2>
        <p>Trips that went over the expected distance, or where the meter photo doesn&apos;t match the entered reading</p>
      </div>

      {alerts.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="flex-between mb16">
            <div className="card-title" style={{ marginBottom: 0 }}>Filters</div>
            {hasActiveFilters && (
              <button type="button" className="btn btn-sm" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>

          <div className="form-grid cols3">
            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Vehicle</label>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                <option value="">All vehicles</option>
                {vehicleOptions.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Driver</label>
              <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
                <option value="">All drivers</option>
                {driverOptions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>From date</label>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                max={to || undefined}
              />
            </div>

            <div className="form-group">
              <label>To date</label>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                min={from || undefined}
              />
            </div>
          </div>

          <p className="text-muted text-sm" style={{ marginTop: 12, marginBottom: 0 }}>
            Showing {filtered.length} of {alerts.length} alert{alerts.length === 1 ? '' : 's'}
          </p>
        </div>
      )}

      {alerts.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="icon">✓</div>
            <p>No alerts. All trips are within expected distance.</p>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="icon">⚑</div>
            <p>No alerts match the current filters.</p>
            {hasActiveFilters && (
              <button type="button" className="btn btn-sm mt16" onClick={clearFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtered.map((t) => {
            const meterMismatch = isMeterMismatch(t);
            const isSuspicious = t.status === 'suspicious' || meterMismatch;
            const meterDiff = meterMismatch && t.meterOcrReading != null
              ? Number(t.meterIn) - Number(t.meterOcrReading)
              : null;
            return (
              <div
                key={t._id}
                className="card"
                style={{
                  borderColor: isSuspicious ? 'rgba(239,68,68,0.4)' : 'rgba(245,158,11,0.4)',
                  borderLeft: `4px solid ${isSuspicious ? 'var(--danger)' : 'var(--warn)'}`
                }}
              >
                <div className="flex-between mb16">
                  <div>
                    <strong style={{ fontSize: 15 }}>{t.vehicle?.name}</strong>
                    <span className="text-muted" style={{ marginLeft: 10 }}>{t.driver?.name}</span>
                    {isKmAlert(t) && (
                      <span className={`badge ${t.status}`} style={{ marginLeft: 10 }}>{t.status}</span>
                    )}
                    {meterMismatch && (
                      <span className="badge suspicious" style={{ marginLeft: 10 }}>Meter mismatch</span>
                    )}
                  </div>
                  <span className="mono text-muted text-sm">
                    {format(new Date(t.date), 'dd MMM yyyy')} · Out {t.outTime} → In {t.inTime}
                  </span>
                </div>

                {meterMismatch && (
                  <div className="alert-meter-line mb16">
                    <div>
                      Guard entered <strong className="mono">{t.meterIn}</strong>, photo shows{' '}
                      <strong className="mono">{t.meterOcrReading}</strong>
                      {meterDiff != null && (
                        <> (difference <strong className="mono">{meterDiff > 0 ? `+${meterDiff}` : meterDiff} km</strong>)</>
                      )}
                    </div>
                    <div className="btn-row mt8">
                      <button type="button" className="btn btn-sm" onClick={() => setPhotoTrip(t)}>
                        View photo
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => markReviewed(t)}
                          disabled={reviewingId === t._id}
                        >
                          {reviewingId === t._id ? 'Saving…' : 'Mark reviewed'}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {isKmAlert(t) && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
                  <div>
                    <div className="stat-label">Expected KM</div>
                    <div className="mono" style={{ fontSize: 20, fontWeight: 600 }}>{t.expectedKM}</div>
                  </div>
                  <div>
                    <div className="stat-label">Actual KM</div>
                    <div className="mono" style={{ fontSize: 20, fontWeight: 600 }}>{t.actualKM}</div>
                  </div>
                  <div>
                    <div className="stat-label">Extra KM</div>
                    <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: 'var(--danger)' }}>+{t.difference}</div>
                  </div>
                  <div>
                    <div className="stat-label">% Over</div>
                    <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: isSuspicious ? 'var(--danger)' : 'var(--warn)' }}>{t.percentOver}%</div>
                  </div>
                </div>
                )}

                {t.stops?.length > 0 && (
                  <div className="text-muted text-sm">
                    Route: <span style={{ color: 'var(--text)' }}>{t.stops.map(s => s.name).join(' → ')}</span>
                  </div>
                )}
                {t.purpose && (
                  <div className="text-muted text-sm mt8">Purpose: {t.purpose}</div>
                )}
                {t.driverStatement && (
                  <div className="text-muted text-sm mt8">
                    Driver statement: <span style={{ color: 'var(--text)' }}>{t.driverStatement}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <MeterPhotoViewer trip={photoTrip} onClose={() => setPhotoTrip(null)} />
    </>
  );
}
